import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
} from 'amazon-cognito-identity-js';
import { environment } from 'src/environments/environment';
import { CognitoService, IUser } from '../../services/cognito.service';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import { loadUser, setUserAndRole, unsetUser } from '../../auth.actions';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { firstValueFrom, race } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { AutoApprovalService } from '../../services/auto-approval.service';
import { User } from 'src/app/shared/models/user.model';
import { AppState } from 'src/app/app.reducer';
import { FirebaseApp } from '@angular/fire/app';
import { getMessaging, getToken } from '@angular/fire/messaging';
import { RegisteredDevicesService } from 'src/app/shared/services/registered-devices.service';
import { UserService } from 'src/app/dashboard/services/user.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { SetupMfaComponent } from 'src/app/dashboard/components/user-profile/setup-mfa/setup-mfa.component';
import { readReturnUrl } from 'src/app/shared/utils/session-tabs';
import { operaComoAprobado } from 'src/app/shared/utils/estado-compliance.util';
import { MarketingOrigenService } from 'src/app/shared/services/marketing-origen.service';
@Component({
  selector: 'app-sign-in',
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.sass'],
})
export class SignInComponent implements OnInit {
  loading: boolean;
  user: IUser;
  unregisteredMail: boolean = false;
  loginForm: FormGroup;
  loginError: boolean = false;
  showPassword: boolean = false;
  token: string | undefined;

  constructor(
    private userService: UserService,
    private registeredDevice: RegisteredDevicesService,
    private setupMFaDialog: MatDialog,
    private afApp: FirebaseApp,
    private store: Store<AppState>,
    private actions$: Actions,
    private router: Router,
    private cognitoService: CognitoService,
    private fb: FormBuilder,
    private spinner: SpinnerService,
    private autoApproval: AutoApprovalService,
    private marketingOrigen: MarketingOrigenService,
  ) {
    this.loginForm = this.fb.group({
      email: new FormControl('', [
        Validators.required,
        Validators.email,
        this.noSpacesValidator,
      ]),
      password: new FormControl('', [Validators.required]),
    });
    this.loading = false;
    this.user = {} as IUser;
  }
  ngOnInit(): void {
    // El link de campana de Marketing tambien puede apuntar al login: quien
    // llega aca y toca "Crear cuenta" queda atribuido igual. No lanza.
    this.marketingOrigen.capturar(new URLSearchParams(window.location.search).get('origen'));
  }

  public onSubmit(form: FormGroup) {
    this.user = {
      email: form.get('email')?.value,
      password: form.get('password')?.value,
      showPassword: false,
      code: '',
      name: '',
    };
    this.signIn();
  }

  requestPermission() {
    const messaging = getMessaging(this.afApp);
    getToken(messaging, { vapidKey: environment.firebase.applicationServerKey })
      .then((currentToken) => {
        if (currentToken) {
          this.token = currentToken;
        }
      })
      .catch(() => {});
  }

  /** Validador personalizado: No permite espacios en el correo */
  noSpacesValidator(control: AbstractControl): ValidationErrors | null {
    return control.value && control.value.includes(' ')
      ? { noSpaces: true }
      : null;
  }

  /** Evita que los usuarios peguen espacios en el campo */
  handlePaste(event: ClipboardEvent) {
    event.preventDefault();
    const text = event.clipboardData?.getData('text') || '';
    this.loginForm.get('email')?.setValue(text.replace(/\s/g, ''));
  }

  public async signIn(): Promise<void> {
    this.unregisteredMail = false;
    this.loginError = false;
    const ref = this.spinner.start();

    try {
      // Inicia sesión con Cognito
      await this.cognitoService.signIn(this.user.email, this.user.password);

      // Obtiene la sesión y el usuario autenticado en paralelo
      const [cognitoSession, cognitoUser] = await Promise.all([
        this.cognitoService.getSession(),
        this.cognitoService.getAuthUser(),
      ]);

      const userGroup =
        cognitoSession?.accessToken?.payload?.['cognito:groups']?.[0];
      const userRole = cognitoUser?.attributes?.['custom:user-group'];

      if (!userGroup || !userRole) {
        throw new Error('❌ No se pudo obtener el grupo o rol del usuario');
      }

      // Actualiza el store con el usuario y su rol, esperando a que el efecto complete
      this.store.dispatch(
        loadUser({ email: this.user.email, role: userGroup }),
      );

      await firstValueFrom(
        race(
          this.actions$.pipe(ofType(setUserAndRole), map(() => true)),
          this.actions$.pipe(ofType(unsetUser), map(() => false)),
        ),
      );

      // Registro de persona sin aprobar: se le da la chance de aprobarse solo
      // antes de entrar, para que no vea "completa tu validación" teniendo todo
      // en regla. Corre dentro del spinner que ya envuelve al login.
      await this.intentarAprobacionAutomatica(cognitoUser, userGroup);

      this.loginError = false;

      // Mostrar modal para configurar MFA si aplica
      if (
        cognitoUser?.preferredMFA === 'NOMFA' &&
        userRole === 'aguacate_operator'
      ) {
        const dialogConfig: MatDialogConfig = {
          disableClose: false,
          autoFocus: true,
          maxHeight: '90vh',
          maxWidth: '100vw',
        };
        this.setupMFaDialog.open(SetupMfaComponent, dialogConfig);
      }

      if (
        userRole === 'aguacate_retail_client' ||
        userRole === 'aguacate_retail_cashier'
      ) {
        this.router.navigate(['/retail/home']);
      } else if (userRole === 'aguacate_operator') {
        // Si la sesión anterior se cortó sola, el operador vuelve a la página
        // en la que estaba. Ver `session-tabs.ts`.
        this.router.navigateByUrl(readReturnUrl() ?? '/wallet');
      } else {
        this.router.navigate(['/wallet']);
      }
    } catch (error) {
      this.loginError = true;
      this.loading = false;
    } finally {
      this.spinner.stop(ref);
    }
  }

  /**
   * Le pide al backend que evalúe el registro del usuario que acaba de entrar.
   *
   * Solo aplica a PERSONAS sin aprobar. El flujo de empresa no se toca: sigue
   * yendo a su pantalla de registro como siempre.
   *
   * Si el backend aprueba, se recarga el usuario en el store para que los
   * guards vean el estado nuevo y lo dejen entrar al dashboard. Sin esto, el
   * store se queda con el estado viejo hasta el próximo login — que es lo que
   * pasa hoy cuando un operador aprueba a alguien que está conectado.
   *
   * No puede romper el login: cualquier problema se traga y el usuario sigue
   * por el camino de siempre.
   */
  private async intentarAprobacionAutomatica(
    cognitoUser: any,
    userGroup: string,
  ): Promise<void> {
    try {
      // Mismo criterio que `activate.guard`: '0' es persona, el resto empresa.
      const esPersona = cognitoUser?.attributes?.['custom:is-company'] === '0';
      if (!esPersona) return;

      const auth: any = await firstValueFrom(
        this.store.select('auth').pipe(take(1)),
      );
      const estado = auth?.user?.estadoComplianceID;
      // Aprobado (3) o bloqueado (4): no hay nada que auto-aprobar.
      if (estado === undefined || operaComoAprobado(auth?.user)) return;

      const aprobado = await this.autoApproval.evaluarMiRegistro();
      if (!aprobado) return;

      // Refrescar el store para que los guards vean el estado aprobado.
      this.store.dispatch(
        loadUser({ email: this.user.email, role: userGroup }),
      );
      await firstValueFrom(
        race(
          this.actions$.pipe(ofType(setUserAndRole), map(() => true)),
          this.actions$.pipe(ofType(unsetUser), map(() => false)),
        ),
      );
    } catch {
      // Silencioso a propósito: esto es una mejora, no un requisito del login.
    }
  }
}
